import type { components } from "@/api/contract";
import { thumbhashToDataUrl } from "@/shared/lib/thumbhash";

type Attachment = components["schemas"]["Attachment"];

/** Max attachments per message, mirroring the backend `maxAttachmentsPerRun`. */
export const MAX_ATTACHMENTS = 10;

/** A composer-side attachment as it moves through upload (local → ready/error). */
export interface ComposerAttachment {
  /** Client-only id for list keys + removal (the server id arrives as `fileId`). */
  localId: string;
  status: "uploading" | "ready" | "error";
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  /** Object URL for an image preview; revoked when the attachment is dropped. */
  previewUrl?: string;
  /** Server file id, set once the upload completes. */
  fileId?: string;
}

/** Normalized shape the thread renders, unifying optimistic + persisted attachments. */
export interface AttachmentView {
  key: string;
  isImage: boolean;
  /** Image src — object URL (optimistic), download URL, or thumbhash data URL. */
  src?: string;
  fileName: string;
  sizeBytes: number;
}

export function isImageMime(mime: string | null | undefined): boolean {
  return (mime ?? "").startsWith("image/");
}

/** Uppercased file extension for the type badge (e.g. "HTML"), or "" if none. */
export function fileExtensionLabel(fileName: string): string {
  const dot = fileName.lastIndexOf(".");
  if (dot <= 0 || dot === fileName.length - 1) return "";
  return fileName.slice(dot + 1).toUpperCase().slice(0, 6);
}

/** Human-readable file size, e.g. "2.4 MB". */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
}

/** Optimistic composer attachments → render views (object-URL previews). */
export function composerAttachmentViews(attachments: ComposerAttachment[]): AttachmentView[] {
  return attachments.map((a) => ({
    key: a.localId,
    isImage: isImageMime(a.mimeType),
    src: a.previewUrl,
    fileName: a.fileName,
    sizeBytes: a.sizeBytes,
  }));
}

/** Persisted server attachments → render views (download URL or thumbhash). */
export function messageAttachmentViews(
  attachments: Attachment[] | undefined,
): AttachmentView[] {
  if (!attachments || attachments.length === 0) return [];
  return attachments.map((a, i) => {
    const isImage = a.kind === "image" || isImageMime(a.mime_type);
    return {
      key: a.id ?? `att-${i}`,
      isImage,
      src: isImage ? (a.url ?? thumbhashToDataUrl(a.thumbhash) ?? undefined) : undefined,
      fileName: a.file_name ?? "file",
      sizeBytes: a.size_bytes ?? 0,
    };
  });
}
